import { useState } from "react";
import { motion } from "framer-motion";
import { ClipboardCheck, Euro, CalendarDays, ArrowUpRight } from "lucide-react";
import DiagnosticoDialog from "@/components/mockups/DiagnosticoDialog";
import OrcamentoDialog from "@/components/mockups/OrcamentoDialog";
import AgendamentoDialog from "@/components/mockups/AgendamentoDialog";
import { useSiteSettings } from "@/hooks/useCms";

const ACTION_DEFAULTS = [
  {
    id: "diagnostico",
    icon: ClipboardCheck,
    title: "Diagnóstico Legal Inteligente",
    desc: "Utilize a nossa Inteligência Artificial para saber as obrigações legais aplicáveis ao seu ativo ou atividade.",
    cta: "Iniciar Diagnóstico",
  },
  {
    id: "orcamento",
    icon: Euro,
    title: "Orçamento Imediato",
    desc: "A nossa Inteligência Artificial ajuda-o a obter o preço de serviços com apenas alguns cliques.",
    cta: "Solicitar Orçamento",
  },
  {
    id: "agendamento",
    icon: CalendarDays,
    title: "Agendar Online",
    desc: "A nossa Inteligência Artificial ajuda-o a solicitar e agendar o serviço com apenas alguns cliques.",
    cta: "Agendar Inspeção",
  },
];

export default function QuickActions() {
  const [dialog, setDialog] = useState(null);
  const settings = useSiteSettings();
  const handleClick = (id) => setDialog(id);
  const close = () => setDialog(null);

  // Textos editáveis via Admin → Textos de Páginas → Home · Hero (fallback aos defaults)
  const ACTIONS = ACTION_DEFAULTS.map((a) => ({
    ...a,
    title: settings?.[`card_${a.id}_title`] || a.title,
    desc: settings?.[`card_${a.id}_desc`] || a.desc,
    cta: settings?.[`card_${a.id}_cta`] || a.cta,
  }));

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5" data-testid="quick-actions-grid">
        {ACTIONS.map((a, i) => {
          const Icon = a.icon;
          return (
            <motion.button
              key={a.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.08 * i, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => handleClick(a.id)}
              data-testid={`quick-action-${a.id}`}
              className="qa-card group text-left px-6 py-5 lg:px-7 lg:py-6 w-full flex flex-col"
            >
              {/* Ícone + Título lado a lado */}
              <div className="flex items-start gap-3.5 mb-3">
                <span
                  className="shrink-0 inline-flex w-10 h-10 items-center justify-center rounded-lg bg-white/[0.03] border border-white/15 text-[#4FC6D8] group-hover:border-[#4FC6D8]/60 group-hover:bg-[#4FC6D8]/10 transition-all duration-300"
                  aria-hidden
                >
                  <Icon size={18} strokeWidth={1.75} />
                </span>
                <h3 className="font-heading text-[1.05rem] lg:text-[1.15rem] font-medium leading-[1.25] tracking-tight text-white pt-0.5">
                  {a.title}
                </h3>
              </div>

              {/* Descrição */}
              <p className="text-[13.5px] sm:text-[12.5px] text-white/60 leading-[1.6] mb-5">
                {a.desc}
              </p>

              {/* Divisor */}
              <span className="mt-auto block h-px w-full bg-white/10 group-hover:bg-[#4FC6D8]/40 transition-colors duration-300" />

              {/* CTA */}
              <span className="mt-3.5 inline-flex items-center justify-between gap-3 text-white group-hover:text-[#4FC6D8] transition-colors duration-300">
                <span className="text-[12.5px] sm:text-[11.5px] font-medium tracking-[0.06em] uppercase">
                  {a.cta}
                </span>
                <ArrowUpRight
                  size={16}
                  strokeWidth={1.75}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </span>
            </motion.button>
          );
        })}
      </div>

      <DiagnosticoDialog open={dialog === "diagnostico"} onClose={close} />
      <OrcamentoDialog open={dialog === "orcamento"} onClose={close} />
      <AgendamentoDialog open={dialog === "agendamento"} onClose={close} />
    </>
  );
}
